import axios from 'axios';
import { User, Appointment, SmsSettings, AmiSettings, AmiDashboard, AmiCdrDebugResult, ScreenPopResponse, QueueLoginStatus } from '../types';
import { getApiBaseUrl, logApiConfig } from '../utils/getApiUrl';

// Auto-detect API URL based on environment
const API_BASE_URL = getApiBaseUrl();

// Log API configuration for debugging
logApiConfig();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  login: (username: string, password: string) =>
    api.post('/auth/login', { username, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  updateMyExtension: (phone_extension: string) =>
    api.put('/auth/me/extension', { phone_extension }),
};

// Users API
export const usersAPI = {
  create: (userData: Partial<User>) =>
    api.post('/users/', userData),
  getOrCreate: (userData: Partial<User>) =>
    api.post('/users/get-or-create', userData),
  getByPhone: (phone: string) =>
    api.get(`/users/phone/${phone}`),
  update: (id: number, userData: Partial<User>) =>
    api.put(`/users/${id}`, userData),
};

// Services API
export const servicesAPI = {
  getAll: () => api.get('/services/'),
  getById: (id: number) => api.get(`/services/${id}`),
};

export const adminAPI = {
  getSmsSettings: () => api.get<SmsSettings>('/admin/settings/sms'),
  updateSmsSettings: (payload: Partial<SmsSettings>) => api.put<SmsSettings>('/admin/settings/sms', payload),
  testSms: (payload: { phone: string; message: string }) => api.post<{ success: boolean }>('/admin/settings/sms/test', payload),
  getBookingSettings: () =>
    api.get<{ booking_enabled: boolean; booking_disabled_message: string }>('/admin/settings/booking'),
  updateBookingSettings: (payload: { booking_enabled: boolean; booking_disabled_message: string }) =>
    api.put<{ booking_enabled: boolean; booking_disabled_message: string }>('/admin/settings/booking', payload),
  getAmiSettings: () => api.get<AmiSettings>('/admin/settings/ami', { timeout: 15000 }),
  updateAmiSettings: (payload: Partial<AmiSettings> & { secret?: string; cdr_db_secret?: string }) =>
    api.put<AmiSettings>('/admin/settings/ami', payload),
  testAmiSettings: (payload?: {
    host?: string;
    port?: number;
    username?: string;
    secret?: string;
  }) => api.post<{ success: boolean; message: string }>('/admin/settings/ami/test', payload ?? {}),
  getAmiDashboard: () =>
    api.get<AmiDashboard>('/admin/ami/dashboard', { timeout: 25000 }),
  getAmiScreenPop: () =>
    api.get<ScreenPopResponse>('/admin/ami/screen-pop', { timeout: 25000 }),
  getQueueLoginConfig: () =>
    api.get<{ feature_enabled: boolean; queue: string }>('/admin/ami/queue-login/config', {
      timeout: 8000,
    }),
  getQueueLoginStatus: () =>
    api.get<QueueLoginStatus>('/admin/ami/queue-login/status', { timeout: 20000 }),
  joinQueueLogin: (extension?: string) =>
    api.post<QueueLoginStatus>(
      '/admin/ami/queue-login/join',
      extension ? { extension } : {},
      { timeout: 20000 }
    ),
  leaveQueueLogin: (extension?: string) =>
    api.post<QueueLoginStatus>(
      '/admin/ami/queue-login/leave',
      extension ? { extension } : {},
      { timeout: 20000 }
    ),
  debugAmiCdr: (phone: string) =>
    api.post<AmiCdrDebugResult>('/admin/ami/cdr-debug', { phone }, { timeout: 180000 }),
  getAuthSessions: (params?: { skip?: number; limit?: number }) =>
    api.get<{
      id: number;
      admin_id: number;
      admin_username: string;
      admin_role: string;
      login_at: string | null;
      logout_at: string | null;
      ip_address: string | null;
      user_agent: string | null;
      duration_seconds: number;
      is_online: boolean;
    }[]>('/admin/auth-sessions', { params }),
  deleteAppointment: (id: number) => api.delete<{ message: string }>(`/admin/appointments/${id}`),
  restoreFromCancel: (id: number) =>
    api.post<{ message: string; new_status: string }>(`/admin/appointments/${id}/restore-from-cancel`),
  searchByNationalId: (nationalId: string) =>
    api.get<Appointment[]>(`/admin/appointments/search/by-national-id/${encodeURIComponent(nationalId)}`),
  searchByTrackingCode: (trackingCode: string) =>
    api.get<Appointment[]>(`/admin/appointments/search/by-tracking-code/${encodeURIComponent(trackingCode)}`),
};

// Appointments API
export const appointmentsAPI = {
  getBookingStatus: () =>
    api.get<{ booking_enabled: boolean; booking_disabled_message: string }>('/appointments/booking-status'),
  create: (appointmentData: Partial<Appointment>) =>
    api.post('/appointments/', appointmentData),
  update: (id: number, updateData: Partial<Appointment>) =>
    api.put(`/appointments/${id}`, updateData),
  getByUser: (userId: number) =>
    api.get(`/appointments/user/${userId}`),
  getById: (id: number) =>
    api.get(`/appointments/${id}`),
  getByNationalId: (nationalId: string) =>
    api.get(`/appointments/national-id/${nationalId}`),
  getByTrackingCode: (trackingCode: string) =>
    api.get(`/appointments/tracking-code/${trackingCode}`),
  updateStep: (id: number, step: number, data: any) =>
    api.put(`/appointments/${id}/step`, { step, data }),
  uploadPrescription: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/appointments/${id}/upload-prescription`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Complaints & feedback API (public)
export const complaintsAPI = {
  lookupPhone: (phone: string) =>
    api.get<{ found: boolean; full_name?: string; user_id?: number }>(
      `/complaints/lookup-phone/${encodeURIComponent(phone)}`
    ),
  create: (data: {
    submission_type?: 'complaint' | 'survey';
    full_name?: string;
    phone: string;
    description?: string;
    survey_answers?: Record<string, string>;
  }) =>
    api.post<{
      id: number;
      tracking_code: string;
      submission_type: 'complaint' | 'survey';
      message: string;
    }>('/complaints/', data),
};

export default api;
